import type {ConsoleDid, SatelliteDid} from '@junobuild/ic-client/actor';
import {assertNonNullish, isBrowser, toNullable} from '@junobuild/utils';
import {UPLOAD_CHUNK_SIZE} from '../constants/upload.constants';
import type {EncodingType} from '../types/storage';
import type {UploadAsset, UploadParams, UploadWithProposalParams} from '../types/upload';

type InitAssetKey = SatelliteDid.InitAssetKey | ConsoleDid.InitAssetKey;
type UploadChunk = SatelliteDid.UploadChunk | ConsoleDid.UploadChunk;
type CommitBatch = SatelliteDid.CommitBatch | ConsoleDid.CommitBatch;

export const uploadAsset = async ({
  asset: {data, headers, ...restAsset},
  actor,
  progress
}: {
  asset: UploadAsset;
} & UploadParams): Promise<void> => {
  const {init_asset_upload, upload_asset_chunk, commit_asset_upload} = actor;

  const {batch_id: batchId} = await init_asset_upload(mapInitAssetUploadParams(restAsset));

  progress?.onInitiatedBatch();

  const {chunkIds} = await uploadChunks({data, uploadFn: upload_asset_chunk, batchId});

  progress?.onUploadedFileChunks(restAsset.fullPath);

  await commitAsset({
    commitFn: commit_asset_upload,
    batchId,
    data,
    headers,
    chunkIds
  });

  progress?.onCommittedBatch();
};

export const uploadAssetWithProposal = async ({
  asset: {data, headers, ...restAsset},
  proposalId,
  actor,
  progress
}: {
  asset: UploadAsset;
} & UploadWithProposalParams): Promise<void> => {
  const {init_proposal_asset_upload, upload_proposal_asset_chunk, commit_proposal_asset_upload} =
    actor;

  progress?.onInitiatedBatch();

  const {batch_id: batchId} = await init_proposal_asset_upload(
    mapInitAssetUploadParams(restAsset),
    proposalId
  );

  const {chunkIds} = await uploadChunks({data, uploadFn: upload_proposal_asset_chunk, batchId});

  progress?.onUploadedFileChunks(restAsset.fullPath);

  await commitAsset({
    commitFn: commit_proposal_asset_upload,
    batchId,
    data,
    headers,
    chunkIds
  });

  progress?.onCommittedBatch();
};

export const uploadAssetsWithProposal = async ({
  assets,
  proposalId,
  actor,
  progress
}: {
  assets: UploadAsset[];
} & UploadWithProposalParams): Promise<void> => {
  const {
    init_proposal_many_assets_upload,
    upload_proposal_asset_chunk,
    commit_proposal_many_assets_upload
  } = actor;

  const batchIds = await init_proposal_many_assets_upload(
    assets.map(mapInitAssetUploadParams),
    proposalId
  );

  progress?.onInitiatedBatch();

  const uploadAssetChunk = async ({
    fullPath,
    data,
    headers
  }: UploadAsset): Promise<BatchToCommit> => {
    const initializedBatch = batchIds.find(([batchIdFullPath]) => batchIdFullPath === fullPath);

    assertNonNullish(initializedBatch);

    const [_, initUpload] = initializedBatch;
    const {batch_id: batchId} = initUpload;

    const {chunkIds} = await uploadChunks({data, uploadFn: upload_proposal_asset_chunk, batchId});

    progress?.onUploadedFileChunks(fullPath);

    return {
      batchId,
      headers,
      chunkIds,
      data
    };
  };

  const batchAndChunkIdsToCommit = await Promise.all(assets.map(uploadAssetChunk));

  await commit_proposal_many_assets_upload(batchAndChunkIdsToCommit.map(mapCommitBatch));

  progress?.onCommittedBatch();
};

const mapInitAssetUploadParams = ({
  filename,
  collection,
  token,
  fullPath,
  encoding,
  description
}: Omit<UploadAsset, 'headers' | 'data'>): InitAssetKey => ({
  collection,
  full_path: fullPath,
  name: filename,
  token: toNullable<string>(token),
  encoding_type: toNullable<EncodingType>(encoding),
  description: toNullable(description)
});

type BatchToCommit = {
  batchId: bigint;
  chunkIds: UploadChunkResult[];
} & Pick<UploadAsset, 'headers' | 'data'>;

const mapCommitBatch = ({batchId, chunkIds, headers, data}: BatchToCommit): CommitBatch => {
  const contentType: [[string, string]] | undefined =
    headers.find(([type, _]) => type.toLowerCase() === 'content-type') === undefined &&
    data.type !== undefined &&
    data.type !== ''
      ? [['Content-Type', data.type]]
      : undefined;

  return {
    batch_id: batchId,
    chunk_ids: chunkIds.map(({chunk_id}: UploadChunkResult) => chunk_id),
    headers: [...headers, ...(contentType ?? [])]
  };
};

const commitAsset = async ({
  commitFn,
  ...rest
}: {
  commitFn: (commitBatch: CommitBatch) => Promise<void>;
  batchId: bigint;
  chunkIds: UploadChunkResult[];
} & Pick<UploadAsset, 'headers' | 'data'>) => {
  const commitBatch = mapCommitBatch(rest);
  await commitFn(commitBatch);
};

const uploadChunks = async ({
  data,
  uploadFn,
  batchId
}: {
  batchId: bigint;
} & Pick<UploadAsset, 'data'> &
  Pick<UploadChunkParams, 'uploadFn'>): Promise<{chunkIds: UploadChunkResult[]}> => {
  const uploadChunks: UploadChunkParams[] = [];

  // Prevent transforming chunk to arrayBuffer error: The requested file could not be read, typically due to permission problems that have occurred after a reference to a file was acquired.
  const clone: Blob = isBrowser() ? new Blob([await data.arrayBuffer()]) : data;

  // Split data into chunks
  let orderId = 0n;
  for (let start = 0; start < clone.size; start += UPLOAD_CHUNK_SIZE) {
    const chunk: Blob = clone.slice(start, start + UPLOAD_CHUNK_SIZE);

    uploadChunks.push({
      batchId,
      chunk,
      uploadFn,
      orderId
    });

    orderId++;
  }

  // Upload chunks to the IC in batch - i.e. 12 chunks uploaded at a time.
  let chunkIds: UploadChunkResult[] = [];
  for await (const results of batchUploadChunks({uploadChunks})) {
    chunkIds = [...chunkIds, ...results];
  }

  return {chunkIds};
};

async function* batchUploadChunks({
  uploadChunks,
  limit = 12
}: {
  uploadChunks: UploadChunkParams[];
  limit?: number;
}): AsyncGenerator<UploadChunkResult[], void> {
  for (let i = 0; i < uploadChunks.length; i = i + limit) {
    const batch = uploadChunks.slice(i, i + limit);
    const result = await Promise.all(batch.map((params) => uploadChunk(params)));
    yield result;
  }
}

interface UploadChunkResult {
  chunk_id: bigint;
}

interface UploadChunkParams {
  batchId: bigint;
  chunk: Blob;
  uploadFn: (uploadChunk: UploadChunk) => Promise<UploadChunkResult>;
  orderId: bigint;
}

const uploadChunk = async ({
  batchId,
  chunk,
  uploadFn,
  orderId
}: UploadChunkParams): Promise<UploadChunkResult> =>
  uploadFn({
    batch_id: batchId,
    content: new Uint8Array(await chunk.arrayBuffer()),
    order_id: toNullable(orderId)
  });
